import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import Modal from '../../../../../incase-domain/components/modals/directory/Modal';
import {Option} from '../../Select';
import {selectedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import Label from '../../../../../incase-domain/components/label/Label';
import DropdownMenuCompact from '../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Button from '../../../../../incase-domain/components/button/Button';
import {IQuestionElements} from '../../../../../incase-domain/elements/question';
import {IElementTextInput} from '../../../../../incase-domain/elements/textInput';
import {IElement} from '../../../../../incase-domain/elements';
import {getQuestions, getReportSourceLabel} from '../../../../../components/slides/highlighted/questionReport/utils';
import {generateErrorMessages, getErrorClasses} from '../../../../../utils/validation';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import RenderQuestionReport from '../../../../../components/elements/question/questionReport/RenderQuestionReport';

interface IQuestionReportModalProps {
    isOpen: boolean;
    onConfirm: (questionElementId?: string, graphSize?: 'sm' | 'md' | 'lg', graphType?: 'bar' | 'pie', slideName?: string) => void;
    onClose: () => void;
}

function QuestionReportModal({isOpen, onConfirm, onClose}: IQuestionReportModalProps): JSX.Element {
    const {t} = useTranslation();
    const presentation = useSelector(selectedPresentationSelector);

    const [selectedOption, setSelectedOption] = useState<Option | null>(null);
    const [errors, setErrors] = useState<IValidationErrorObject | null>(null);

    const questions = useMemo(() => getQuestions(presentation), [presentation]);

    const slide = useMemo(
        () => presentation.slides.find((slide) => slide.elements.find((el) => el.id === selectedOption?.value)),
        [presentation.slides, selectedOption?.value]
    );

    const question = useMemo(
        () => slide?.elements.find((el) => el.id === selectedOption?.value) as IQuestionElements | IElementTextInput | undefined,
        [slide?.elements, selectedOption?.value]
    );

    const selectedQuestion = useMemo(() => questions.find((q) => q.id === selectedOption?.value), [questions, selectedOption?.value]);

    function mapToOption(source: IElement): Option {
        return {label: getReportSourceLabel(source), value: source.id};
    }

    const handleConfirm = (): void => {
        if (!selectedOption) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const errors: any = {
                questionElementId: ['designSidepanel.questionReportWithoutQuestion'],
            };
            setErrors(errors);
            return;
        }

        onConfirm(selectedOption?.value as string);
        setSelectedOption(null);
        setErrors(null);
    };

    const handleCancel = (): void => {
        onClose();
        setSelectedOption(null);
        setErrors(null);
    };

    const chooseQuestionItems = [
        questions.map((el) => ({
            label: getReportSourceLabel(el),
            onClick: () => {
                setSelectedOption(mapToOption(el));
                setErrors(null);
            },
        })),
    ];

    return (
        <Modal
            isOpen={isOpen}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            header={t('presEditPage.editSidebar.elements.questionReport')}
            description={t('presEditPage.editSidebar.elements.questionReportDescription')}
            showCancel
            showConfirm
            closeOnClickOutside={false}
            size="lg"
            icon="solar:chart-square-linear"
        >
            <div className="c-questionReportModal">
                <div className="c-questionReportModal__navigation l-group--form">
                    <div className={`l-group__item ${getErrorClasses(errors, 'questionElementId')}`}>
                        <Label>{t('designSidepanel.chooseQuestion')}</Label>
                        <DropdownMenuCompact
                            width="full"
                            key={selectedQuestion && getReportSourceLabel(selectedQuestion)}
                            className="c-sidepanel__dropdown"
                            items={chooseQuestionItems}
                        >
                            <Button
                                fullWidth
                                size="xs"
                                trailing="solar:alt-arrow-down-linear"
                                variant="soft"
                                className="c-sidepanel__dropdown_button"
                            >
                                {(selectedQuestion && mapToOption(selectedQuestion))?.label || t('designSidepanel.chooseQuestion')}
                            </Button>
                        </DropdownMenuCompact>

                        {generateErrorMessages(errors, 'questionElementId')}
                    </div>
                </div>
                <div className="c-questionReportModal__preview">
                    <RenderQuestionReport question={question} slide={slide} device={DeviceType.DESKTOP} />
                </div>
            </div>
        </Modal>
    );
}

export default QuestionReportModal;
